import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import HostedPaymentMock from '@/components/HostedPaymentMock';
import { CustomerShell } from '@/screens/customer-flow/SelfBookPage';
import {
  fetchPublicDeposit,
  payPublicDeposit,
  payPublicTopup,
  type PublicDepositBundle,
} from '@/api/publicDeposit';
import { SESSION_META } from '@/domain/pricing';
import { dayKey } from '@/utils/studioDay';
import { useSimulationStore } from '@/state/simulationStore';
import { aud } from '@/utils/format';
import { isEmail, isPlaceholderName } from '@atink/domain';

// Customer-facing pay page for a sent deposit link. PUBLIC + self-contained
// (tv-k9b.8): it renders with NO staff login, so it loads and pays its one deposit
// through the public /api/public/deposit/:id endpoints — NOT the authed
// /api/bootstrap + pay-deposit intent, which 401 for a customer and left the page
// dead on staging (it only "worked" locally because the dev proxy injects a token).
//
// PLAN-AWARE (tv-2na.8.10): this is the one link the client gets — the Agent's
// deposit-link SMS and every top-up reminder. For a pay-over-time deposit it asks
// for the instalment DUE NOW (never the full deposit again) and pays it through
// the public pay-topup endpoint (the same transaction as the staff intent). A
// part-paid deposit stays on the plan view until the plan is paid in full.

/** The instalment the page should take now, or null when this is not a live plan. */
function planDueNow(bundle: PublicDepositBundle): { dueIso: string; amountAud: number } | null {
  const plan = bundle.topupPlan;
  if (!plan || plan.status !== 'active') return null;
  return plan.nextDue;
}

export default function DepositPayPage() {
  const { depositId } = useParams<{ depositId: string }>();
  // undefined = still loading; null = genuinely absent (404 / load error).
  const [bundle, setBundle] = useState<PublicDepositBundle | null | undefined>(undefined);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const [paymentReceived, setPaymentReceived] = useState(false);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [payError, setPayError] = useState<string | null>(null);
  const [payAttempt, setPayAttempt] = useState(0); // bump remounts HostedPaymentMock to its idle state
  const [slotTaken, setSlotTaken] = useState(false); // B4 7g3y: a 409 — the time has gone, no retry
  // 7ktj: the paid bundle the server returned, swapped in once the mock confirms.
  const paidBundle = useRef<PublicDepositBundle | null>(null);

  useEffect(() => {
    let alive = true;
    setPaymentReceived(false);
    if (!depositId) {
      setBundle(null);
      return;
    }
    fetchPublicDeposit(depositId)
      .then((b) => {
        if (alive) setBundle(b);
      })
      .catch(() => {
        // Transport error — treat as absent; the customer can refresh / get in touch.
        if (alive) setBundle(null);
      });
    return () => {
      alive = false;
    };
  }, [depositId, clockIso]);

  // Whatever is already on file for THIS deposit's customer is pre-filled (still
  // editable): the name (never an IG `@handle` placeholder), the mobile and the
  // email (D05-11, Anas 6 Oct: an email is mandatory when a deposit is taken).
  const onFile = bundle?.customer ?? null;
  useEffect(() => {
    if (!onFile) return;
    const placeholder = onFile.placeholderName ?? isPlaceholderName(onFile.firstName, onFile.lastName);
    if (!placeholder) setName((n) => n || `${onFile.firstName} ${onFile.lastName}`.trim());
    setMobile((m) => m || onFile.phone || '');
    setEmail((e) => e || onFile.email || '');
  }, [onFile]);

  // Still loading — a distinct state, NOT the "expired" message (which used to
  // flash before hydration finished — tv-k9b.6 / F6).
  if (bundle === undefined) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center" data-testid="deposit-loading">
          Loading your deposit…
        </p>
      </CustomerShell>
    );
  }

  if (bundle === null) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center" data-testid="deposit-missing">
          This deposit link has expired or was not generated yet. Get in touch with the studio.
        </p>
      </CustomerShell>
    );
  }

  const { deposit, booking, artistName: rawArtistName, studioName } = bundle;
  const artistName = rawArtistName ?? deposit.artistId ?? 'your artist';
  const sessionLabel =
    booking && booking.sessionLength in SESSION_META
      ? SESSION_META[booking.sessionLength as keyof typeof SESSION_META].label
      : 'Session';
  const whenLabel = booking?.start ? format(new Date(booking.start), 'EEEE d MMM · h:mm a') : '';

  const plan = bundle.topupPlan ?? null;
  const dueNow = planDueNow(bundle);
  // What a plain link still owes (the server charges exactly this): a part-paid
  // link asks for the balance, never the full deposit again. An older bundle with
  // no owedAud reads a paid deposit as settled.
  const owed = deposit.owedAud ?? (deposit.status === 'paid' ? 0 : deposit.amountAud);
  const partPaid = !dueNow && owed > 0 && owed < deposit.amountAud;
  const payAud = dueNow ? dueNow.amountAud : owed;

  // A deposit link always carries a date and an artist (diagram 03; Anas 1 Oct,
  // 7g3y). The seed's older dateless link can't be paid: say so plainly, with no
  // pay button (the server refuses the payment too).
  if (deposit.linkState === 'needs-date') {
    return (
      <CustomerShell>
        <p className="label-small-caps text-gold-bright text-center">Deposit link</p>
        <div
          className="mt-4 rounded-md border border-gold-dim/40 bg-ink-deep p-4 text-center"
          data-testid="deposit-needs-date"
        >
          <p className="font-serif text-cream">This link needs a date.</p>
          <p className="font-serif text-cream-soft text-sm mt-1">
            There’s no date on it yet, so it can’t be paid. The studio will be in touch to book you in.
          </p>
        </div>
      </CustomerShell>
    );
  }

  // Money on file with no date (an Ops cancel, a no-date release): it stays on file.
  if (deposit.status === 'paid' && !dueNow && owed <= 0 && !booking) {
    return (
      <CustomerShell>
        <div className="text-center py-6" data-testid="deposit-on-file">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">Deposit paid · kept on file</h1>
          <p className="font-serif text-cream-soft mt-2">
            You don’t have a date yet. We’ll keep your deposit on file for 12 months, and the studio will be in touch to
            book you in with {artistName}.
          </p>
        </div>
      </CustomerShell>
    );
  }

  if (deposit.status === 'paid' && !dueNow && owed <= 0) {
    return (
      <CustomerShell>
        <div className="text-center py-6" data-testid="deposit-paid">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">Deposit paid</h1>
          <p className="font-serif text-cream-soft mt-2">
            You’re booked with {artistName}. We’ve texted your confirmation.
          </p>
          {whenLabel ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-mint/40 bg-mint/10 px-3 py-2">
              <span className="font-inter text-xs text-mint">
                {whenLabel}
                {studioName ? ` · ${studioName}` : ''}
              </span>
            </div>
          ) : null}
        </div>
      </CustomerShell>
    );
  }

  // A pay-at-studio hold is settled in person (no online form).
  if (deposit.payAtStudio) {
    return (
      <CustomerShell>
        <p className="label-small-caps text-gold-bright text-center">Deposit hold</p>
        <div
          className="mt-4 rounded-md border border-gold-dim/40 bg-ink-deep p-4 text-center"
          data-testid="deposit-pay-at-studio"
        >
          <p className="font-serif text-cream">You’re all set to settle this deposit in person.</p>
          <p className="font-serif text-cream-soft text-sm mt-1">
            Pay at the studio when you arrive — no online payment needed.
          </p>
        </div>
      </CustomerShell>
    );
  }

  // Another client paid for this time first (D02-07 / D05-19): the server
  // released this hold, so say so plainly, with no pay form. The studio's Agent
  // has been alerted to call back with a new date.
  if (deposit.linkState === 'slot-taken') {
    return (
      <CustomerShell>
        <p className="label-small-caps text-gold-bright text-center">Deposit link</p>
        <div
          className="mt-4 rounded-md border border-blood/50 bg-blood/10 p-4 text-center"
          data-testid="deposit-slot-taken"
        >
          <p className="font-serif text-cream">That time has been booked by someone else.</p>
          <p className="font-serif text-cream-soft text-sm mt-1">
            No charge was made. The studio will call you to find a new date{artistName ? ` with ${artistName}` : ''}.
          </p>
        </div>
      </CustomerShell>
    );
  }

  // Anything other than a live pending deposit is no longer payable, and a link
  // past its pay-by has expired even before the sweep runs (B4 7g3y): the server
  // decides it (`linkState`) with the same rule the ledger and Ops use, and refuses
  // the payment too.
  if (
    deposit.linkState === 'expired' ||
    (deposit.status !== 'pending' && !dueNow && !(deposit.status === 'paid' && owed > 0))
  ) {
    return (
      <CustomerShell>
        <p className="label-small-caps text-gold-bright text-center">Deposit link</p>
        <div
          className="mt-4 rounded-md border border-blood/50 bg-blood/10 p-4 text-center"
          data-testid="deposit-expired"
        >
          <p className="font-serif text-cream">This link has expired.</p>
          <p className="font-serif text-cream-soft text-sm mt-1">
            The hold on this slot has lapsed. Message us and we’ll send a fresh link.
          </p>
        </div>
      </CustomerShell>
    );
  }

  const paidCount = plan?.instalments.filter((i) => i.status === 'paid').length ?? 0;
  const waiting = dueNow && paidCount > 0 && (
    paymentReceived || dayKey(dueNow.dueIso) > dayKey(bundle.nowIso ?? clockIso)
  );
  if (waiting && plan && dueNow) {
    return (
      <CustomerShell>
        <div className="text-center py-6" data-testid="deposit-instalment-paid">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">
            {paidCount === 1 ? 'First instalment paid' : `Instalment ${paidCount} paid`}
          </h1>
          <p className="font-serif text-cream-soft mt-2">{aud(plan.paidAud)} of {aud(plan.totalAud)} paid. Your booking is secure.</p>
          <p className="font-serif text-cream mt-4" data-testid="deposit-next-instalment">
            Next instalment: {aud(dueNow.amountAud)} on {format(new Date(`${dayKey(dueNow.dueIso)}T12:00:00`), 'EEEE d MMM')}.
          </p>
          <p className="font-serif text-cream-soft mt-2">We’ll send you a reminder with your payment link.</p>
          {whenLabel ? <p className="font-serif text-cream-soft mt-4">{whenLabel} · {artistName}{studioName ? ` · ${studioName}` : ''}</p> : null}
        </div>
      </CustomerShell>
    );
  }

  const detailsComplete = name.trim().length > 0 && mobile.trim().length >= 6 && isEmail(email);

  // 7ktj: the hosted mock shows "Payment confirmed" only after the server took
  // the money (settle), then hands over (onPaid) to swap in the paid bundle.
  const onPaid = () => {
    const next = paidBundle.current;
    if (!next) return;
    paidBundle.current = null;
    setPaymentReceived(Boolean(dueNow));
    setBundle(next);
  };
  const settle = async (): Promise<boolean> => {
    setPayError(null);
    const details = { name: name.trim(), mobile: mobile.trim(), email: email.trim() };
    const res = dueNow
      ? await payPublicTopup(deposit.id, details)
      : await payPublicDeposit(deposit.id, details);
    if (res.ok) {
      // The server returns the refreshed bundle (deposit paid / the plan's next
      // instalment, booking confirmed); swap it in so the page moves on.
      paidBundle.current = res.bundle ?? { ...bundle, deposit: { ...deposit, status: 'paid' } };
      return true;
    }
    if (res.status === 410) {
      // The link expired while the page was open: say so, with no pay button.
      setBundle({ ...bundle, deposit: { ...deposit, linkState: 'expired' } });
    } else if (res.error === 'deposit-link-needs-date') {
      // The link has no date (7g3y): nothing was taken; say so, with no pay button.
      setBundle({ ...bundle, deposit: { ...deposit, linkState: 'needs-date' } });
    } else if (res.error === 'email-required') {
      // D05-11: the server takes no money without a usable email; nothing was charged.
      setPayError('Please check your email address. We need one to take your deposit. No charge was made.');
      setPayAttempt((n) => n + 1);
    } else if (res.status === 409) {
      setPayError('That time has gone — someone else has just booked it. No charge was made; the studio will be in touch.');
      setSlotTaken(true);
    } else {
      setPayError("Your payment didn't go through — no charge was made. Please try again.");
      setPayAttempt((n) => n + 1); // remount HostedPaymentMock to idle so it's retryable
    }
    return false;
  };

  return (
    <CustomerShell>
      <p className="label-small-caps text-gold-bright text-center">
        {dueNow ? 'Your deposit plan' : 'Your deposit'}
      </p>
      <h1 className="heading-display text-3xl text-cream text-center mt-1" data-testid="deposit-amount">
        {aud(payAud)}
      </h1>
      {dueNow && plan ? (
        <p className="font-serif text-cream-soft text-center text-sm mt-2" data-testid="deposit-plan-outstanding">
          Due {format(new Date(`${dayKey(dueNow.dueIso)}T12:00:00`), 'EEE d MMM')} · instalment{' '}
          {plan.instalments.filter((i) => i.status === 'paid').length + 1} of {plan.instalments.length} ·{' '}
          {aud(plan.outstandingAud)} of {aud(plan.totalAud)} still to go
        </p>
      ) : partPaid ? (
        <p className="font-serif text-cream-soft text-center text-sm mt-2" data-testid="deposit-owed-note">
          Still owed on your deposit · {aud(deposit.amountAud - owed)} of {aud(deposit.amountAud)} already paid
        </p>
      ) : (
        <p className="font-serif text-cream-soft text-center text-sm mt-2">
          Balance settled with your artist on the day.
        </p>
      )}
      {deposit.dateless && !dueNow && partPaid ? (
        <p
          className="mt-3 rounded-md border border-gold-dim/40 bg-ink-deep p-3 text-center font-serif text-sm text-cream-soft"
          data-testid="deposit-on-file-note"
        >
          No date is booked yet. Your deposit stays on file for 12 months, and the studio will be in touch to book you in.
        </p>
      ) : null}

      <div className="mt-6 panel p-4" data-testid="deposit-slot">
        <p className="font-cinzel text-lg text-cream">{artistName}</p>
        {studioName ? (
          <p className="font-inter text-[11px] text-cream-soft uppercase tracking-widest">
            {studioName} studio
          </p>
        ) : null}
        <div className="mt-3 border-t border-gold-dim/20 pt-3 space-y-1">
          {whenLabel ? <p className="font-serif text-sm text-cream">{whenLabel}</p> : null}
          <p className="font-serif text-sm text-cream-soft">{sessionLabel}</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <p className="label-small-caps text-gold-bright">Your details</p>
        <label className="block">
          <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">Full name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="deposit-name"
            className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          />
        </label>
        <label className="block">
          <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">Mobile</span>
          <input
            type="tel"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            data-testid="deposit-mobile"
            className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          />
        </label>
        <label className="block">
          <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="deposit-email"
            autoComplete="email"
            className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          />
        </label>
        <p className="font-serif text-cream-soft text-xs">
          So we can text your confirmation and a reminder before the day, and email your booking details.
        </p>
      </div>

      <div className="mt-6">
        {payError ? (
          <p
            className="mb-3 rounded-md border border-blood/50 bg-blood/10 p-3 text-center font-serif text-sm text-cream"
            data-testid={slotTaken ? 'deposit-slot-taken' : 'deposit-pay-error'}
          >
            {payError}
          </p>
        ) : null}
        {slotTaken ? null : detailsComplete ? (
          <HostedPaymentMock
            // Remount per retry AND per plan instalment, so the next one starts idle.
            key={`${payAttempt}-${dueNow?.dueIso ?? 'deposit'}`}
            amountAud={payAud}
            description={
              dueNow
                ? `Deposit instalment for your ${sessionLabel.toLowerCase()} with ${artistName}.`
                : `Deposit for your ${sessionLabel.toLowerCase()} with ${artistName}.`
            }
            testid="deposit-hosted-payment"
            settle={settle}
            onConfirm={onPaid}
          />
        ) : (
          <button
            type="button"
            disabled
            data-testid="deposit-pay-locked"
            className="btn-primary w-full opacity-50"
          >
            Add your name, mobile &amp; email to pay
          </button>
        )}
      </div>
    </CustomerShell>
  );
}
